import { DiscountApplyEvent, DiscountRemoveEvent, DiscountUpdateEvent, ThemeEvents } from '@theme/events'
if (!customElements.get('coupons-list')) {
  class CouponsList extends HTMLElement {
    connectedCallback () {
      this.couponCards = Array.from(this.querySelectorAll('[data-coupon-card]'));
      this.currentCode = (this.dataset.currentCode || '').trim();
      this.scope = this.dataset.scope || 'all';

      this.addEventListener('click', this.handleClick);
      // host -> list: applied code changed (after /cart/update.js succeeds)
      // this.addEventListener(theme.events.couponApplied, (e) => this.update(e.detail?.code || ''));
      // host -> list: I'm done, please restore the button (always fires, even on error)
      // this.addEventListener(theme.events.couponResolved, this.onResolved);
      // host -> list: discount update failed (invalid code, shipping discount, etc.)
      // Mirrors `coupon:resolved` for error paths that don't go through the modal.
      document.addEventListener(ThemeEvents.discountUpdateError, this.onDiscountError);
    }

    /**
     * <coupons-list> doesn't extend `Component` — it extends `HTMLElement` — so
     * there is no `super.disconnectedCallback()` to chain. We still need to
     * drop the document-level listener, otherwise it keeps firing on a detached
     * element after a section re-render and the closure keeps a stale `this`
     * (and stale `this.couponCards` references) alive.
     */
    disconnectedCallback () {
      document.removeEventListener(ThemeEvents.discountUpdateError, this.onDiscountError);
    }

    /**
     * User clicked an apply/remove button. We DON'T know what the host should do
     * (PDP scope => copy to clipboard; drawer/page => call /cart/update.js).
     * Hand it up and let the host decide.
     *
     * UX contract: the list itself owns the button's loading state. As soon as
     * a click fires `coupon:action`, the button gets `is-loading` + `disabled`.
     * The host MUST fire `coupon:resolved` (with the same `button` in detail)
     * when its async work finishes — success or error, modal-closed or not —
     * and the list will restore the button.
     */
    handleClick = (evt) => {
      const btn = evt.target.closest('[data-coupon-action]');
      if (!btn) return;
      const card = btn.closest('[data-coupon-card]');
      if (!card) return;
      if (btn.disabled || btn.classList.contains('is-loading')) return;

      evt.stopPropagation();
      this.markLoading(btn);

      let action = this.scope === "pdp" ? "copy" : card.dataset.state === 'applied' ? 'remove' : 'apply'
      let code = (card.dataset.code || '').trim()
      if (this.scope === "pdp") {
        this.handleCopy(btn, code)
        return
      }
      // this.dispatchEvent(new CustomEvent(theme.events.couponAction, {
      //   bubbles: true,
      //   composed: true,
      //   detail: {
      //     code,
      //     action,
      //     button: btn,
      //     card,
      //     scope: this.scope
      //   }
      // }));
      this.dispatchEvent(action === "apply" ? new DiscountApplyEvent(code) : new DiscountRemoveEvent(code))
    }

    markLoading (btn) {
      // Snapshot the original text so `onResolved` can restore it.
      btn.dataset.originalText = btn.textContent;
      btn.classList.add('is-loading');
      btn.disabled = true;
      if (!btn.dataset.loadingText) {
        btn.textContent = '…';
      }
    }

    onResolved = (e) => {
      const btn = e.detail?.button;
      if (!btn || !this.contains(btn)) return;
      btn.classList.remove('is-loading');
      btn.disabled = false;
      if (btn.dataset.originalText) {
        btn.textContent = btn.dataset.originalText;
        delete btn.dataset.originalText;
      }
    }

    /**
     * Host reported that a discount update failed. Find the card whose button
     * is currently `is-loading` (matched by the failed code when available)
     * and restore it through the same path `onResolved` uses.
     *
     * Belt-and-suspenders: `onResolved` already checks `this.contains(btn)`, so
     * even a global query is safe across multiple list instances on the page.
     */
    onDiscountError = (e) => {
      const failedCode = (e.detail?.code || '').trim().toLowerCase();
      let btn = null;

      if (failedCode) {
        const card = this.couponCards.find(
          (c) => (c.dataset.code || '').trim().toLowerCase() === failedCode
        );
        btn = card ? card.querySelector('[data-coupon-action].is-loading') : null;
      }

      // Fallback: there is normally only one loading button at a time.
      if (!btn) btn = this.querySelector('[data-coupon-action].is-loading');
      if (!btn) return;

      this.onResolved({ detail: { button: btn } });
    }

    /**
     * Reflect a new applied code. Called either by host via `coupon:applied`
     * event, or directly (`this.update(code)`).
     *
     * IMPORTANT: never downgrade a button that is mid-async — keep its loading
     * state until the host fires `coupon:resolved`. Otherwise `update()` from
     * a stale response could clear `is-loading` while the modal is still busy.
     */
    update (newCode) {
      this.dataset.currentCode = (newCode || '').trim();
      this.currentCode = this.dataset.currentCode;

      const normalized = this.currentCode.toLowerCase();
      const strings = (window.theme && window.theme.strings && window.theme.strings.coupons) || {};

      this.couponCards.forEach((card) => {
        const code = (card.dataset.code || '').trim();
        const isApplied = Boolean(code && normalized === code.toLowerCase());

        // 1. applied badge
        const badge = card.querySelector('[data-applied]');
        if (badge) {
          badge.classList.toggle('tw-hidden', !isApplied);
          badge.classList.toggle('tw-inline-flex', isApplied);
        }

        // 2. action button (state + class + text)
        const actionBtn = card.querySelector('[data-coupon-action]');
        if (!actionBtn) return;
        if (actionBtn.classList.contains('is-loading')) return;

        const isIneligible = card.dataset.state === 'ineligible';

        if (isApplied) {
          card.dataset.state = 'applied';
          actionBtn.className = 'tw-text-xs tw-underline tw-text-black/70 tw-bg-transparent tw-border-0 tw-cursor-pointer js-remove-coupon';
          actionBtn.textContent = strings.remove || 'Remove';
          actionBtn.disabled = false;
          actionBtn.dataset.action = 'remove';
        } else {
          card.dataset.state = isIneligible ? 'ineligible' : 'eligible';
          actionBtn.className = 'btn btn--primary btn--sm js-apply-coupon';
          actionBtn.textContent = strings.apply || 'Apply';
          actionBtn.disabled = isIneligible;
          actionBtn.dataset.action = 'apply';
        }

        actionBtn.setAttribute('type', 'button');
        actionBtn.setAttribute('data-code', code);
      });
    }

    async handleCopy (button, code) {
      try {
        await theme.utils.copyToClipboard(code);
        this.flash(button, theme.strings?.coupons?.copied || 'Copied!');
      } catch {
        this.flash(button, 'Copy failed', true);
      } finally {
        // Always tell the list we're done so it removes `is-loading`.
        // Re-dispatch the event bubbling up to the list (it's listening on `this`).
        // button.dispatchEvent(new CustomEvent(theme.events.couponResolved, {
        //   bubbles: true,
        //   composed: true,
        //   detail: { button }
        // }));
      }
    }
    flash (btn, msg, isError) {
      btn.textContent = msg;
      btn.classList.toggle('is-error', !!isError);
      btn.classList.remove('is-loading');
      clearTimeout(btn.__flashTimer);
      btn.__flashTimer = setTimeout(() => {
        btn.classList.remove('is-error');
        btn.disabled = false;
        if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
        }
      }, 1600);
    }
  }

  customElements.define('coupons-list', CouponsList);
}
